// Maps Supabase snake_case column names to the human-readable display names
// that consuming components expect. Updated for catalog-rebuild-v2 — only
// columns that are real buyer decision points are listed here. Boolean
// columns are surfaced; enum columns (product_type, power_source,
// water_heating_method, primary_control_type, nozzle_material) are looked up
// via their dedicated `*_LABELS` constants in `app/types/index.ts`.
export const FEATURE_FIELD_MAP: Record<string, string> = {
  // Wash configuration
  self_cleaning_nozzle: 'Self-cleaning nozzle',
  adjustable_nozzle_position: 'Adjustable nozzle position',
  adjustable_spray_pressure: 'Adjustable spray pressure',
  // Wash modes
  has_feminine_wash: 'Feminine wash',
  has_oscillation: 'Oscillation',
  has_pulse: 'Pulse / massage',
  has_turbo_wash: 'Turbo / vortex wash',
  has_pre_rinse: 'Pre-rinse',
  // Comfort
  heated_seat: 'Heated seat',
  warm_air_dryer: 'Warm air dryer',
  // Hygiene
  deodorizer: 'Deodorizer',
  uv_sterilization: 'UV sterilization',
  // Controls
  has_companion_app: 'Companion mobile app',
  programmable_presets: 'Programmable presets',
  night_light: 'Night light',
  // Convenience
  seat_soft_close: 'Soft-close seat',
  seat_auto_open: 'Auto-open seat',
  seat_auto_close: 'Auto-close seat',
  quick_release: 'Quick-release for cleaning',
  // Quality / accessibility
  warranty_is_lifetime: 'Lifetime warranty',
  commercial_grade: 'Commercial-grade',
  ada_compliant: 'ADA-compliant',
  left_handed_friendly: 'Left-handed friendly',
  water_temperature_adjustable: 'Adjustable water temperature',
};

export interface ProductImage {
  id: string;
  category: string;
  isMain: boolean;
  originalSource: string;
  localPath: string;
  filename: string;
}

export interface ProductVariationReseller {
  resellerId: string;
  resellerName: string;
  resellerSlug: string;
  price: number;
  url: string;
  shippingDays: number;
  inStock: boolean;
}

export interface ProductVariation {
  id: string;
  color: string;
  style: string;
  msrp: number;
  resellerUrl: string;
  resellerPrice: number;
  resellerShippingDays: number;
  passthroughTax: boolean;
  isDefaultVariation: boolean;
  brandUrl: string;
  images: ProductImage[];
  resellers: ProductVariationReseller[];
  // Catalog-rebuild-v2: per-variation BNY install eligibility.
  bnyInstallStatus: 'installable' | 'listed_only' | 'discontinued';
  bnyNotInstallReason: string | null;
  bnyNotInstallNote: string | null;
  // Optional property overrides are supported by the shared warning engine
  // even though the current live schema keeps these fields on products.
  power_source?: string | null;
  primary_control_type?: string | null;
  compatible_toilet_shape?: string | null;
  requires_electricity_to_function?: boolean | string | number | null;
  has_required_remote_control?: boolean | string | number | null;
}

export interface Product {
  id: string;
  manufacturer: string;
  manufacturerUrl: string;
  images: ProductImage[];
  variations: ProductVariation[];
  'Product ID': string;
  'Live on BNY': boolean;
  'Tier': string;
  'Category': string;
  'Brand': string;
  'Model': string;
  'Model Name': string;
  'Subtitle': string;
  'Title': string;
  'Overview': string;
  'Why We Love It': string;
  'Installation Type': string;
  power_source: string | null;
  primary_control_type: string | null;
  compatible_toilet_shape: string | null;
  requires_electricity_to_function?: boolean | string | number | null;
  has_required_remote_control?: boolean | string | number | null;
  [key: string]: any;
}

function mapImage(img: any): ProductImage {
  // image_url now points to Supabase Storage
  const imageUrl = img.image_url || '';
  return {
    id: img.airtable_id || img.id,
    category: img.category || '',
    isMain: img.is_main_image || false,
    originalSource: imageUrl,
    localPath: imageUrl,
    filename: img.image_filename || '',
  };
}

function mapVariation(v: any, variationImages: any[], variationResellers: any[]): ProductVariation {
  // Sort resellers by priority (lowest = preferred) and find first in-stock
  const sorted = [...variationResellers].sort(
    (a, b) => (a.resellers?.priority ?? 999) - (b.resellers?.priority ?? 999)
  );
  const preferred = sorted.find(r => r.in_stock !== false) || sorted[0];

  return {
    id: v.variation_id || v.id,
    color: v.color || '',
    style: v.style || '',
    msrp: Number(v.msrp) || 0,
    resellerUrl: preferred?.url || '',
    resellerPrice: Number(preferred?.price) || 0,
    resellerShippingDays: Number(preferred?.shipping_days) || 0,
    passthroughTax: v.passthrough_tax || false,
    isDefaultVariation: v.is_default_variation || false,
    brandUrl: v.brand_url || '',
    images: variationImages.map(img => mapImage(img)),
    bnyInstallStatus: v.bny_install_status || 'installable',
    bnyNotInstallReason: v.bny_not_install_reason || null,
    bnyNotInstallNote: v.bny_not_install_note || null,
    power_source: v.power_source ?? null,
    primary_control_type: v.primary_control_type ?? null,
    compatible_toilet_shape: v.compatible_toilet_shape ?? null,
    requires_electricity_to_function: v.requires_electricity_to_function ?? null,
    has_required_remote_control: v.has_required_remote_control ?? null,
    resellers: sorted.map(r => ({
      resellerId: r.reseller_id,
      resellerName: r.resellers?.name || '',
      resellerSlug: r.resellers?.slug || '',
      price: Number(r.price) || 0,
      url: r.url || '',
      shippingDays: Number(r.shipping_days) || 0,
      inStock: r.in_stock ?? true,
    })),
  };
}

export async function fetchAllProducts(): Promise<Product[]> {
  const res = await fetch('/.netlify/functions/public-api?endpoint=products');
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(`Failed to fetch products: ${err.error}`);
  }

  const { products, variations, images, resellerPrices } = await res.json();

  // Group variations, images, and reseller prices
  const variationsByProduct = new Map<string, any[]>();
  const imagesByProduct = new Map<string, any[]>();
  const imagesByVariation = new Map<string, any[]>();
  const resellersByVariation = new Map<string, any[]>();

  for (const rp of resellerPrices || []) {
    const list = resellersByVariation.get(rp.variation_id) || [];
    list.push(rp);
    resellersByVariation.set(rp.variation_id, list);
  }

  for (const v of variations || []) {
    const list = variationsByProduct.get(v.product_id) || [];
    list.push(v);
    variationsByProduct.set(v.product_id, list);
  }

  for (const img of images || []) {
    if (img.variation_id) {
      const list = imagesByVariation.get(img.variation_id) || [];
      list.push(img);
      imagesByVariation.set(img.variation_id, list);
    } else {
      const list = imagesByProduct.get(img.product_id) || [];
      list.push(img);
      imagesByProduct.set(img.product_id, list);
    }
  }

  return (products || []).map((p: any) => {
    const brandName = (p.brands as any)?.name || '';
    const productImages = (imagesByProduct.get(p.id) || []).map(img => mapImage(img));
    const productVariations = (variationsByProduct.get(p.id) || []).map(v =>
      mapVariation(v, imagesByVariation.get(v.id) || [], resellersByVariation.get(v.id) || [])
    );

    const mapped: Product = {
      id: p.product_id,
      manufacturer: brandName,
      manufacturerUrl: p.manufacturer_url || '',
      images: productImages,
      variations: productVariations,
      'Product ID': p.product_id,
      'Live on BNY': p.live_on_bny || false,
      'Tier': p.tier || '',
      // Catalog-rebuild-v2: legacy `category` column was renamed to
      // `product_type` with a tightened controlled vocabulary. Keeping the
      // display key as 'Category' for backwards compat with downstream UI
      // until we tour every consumer.
      'Category': p.product_type || '',
      'Brand': brandName,
      'Model': p.model || '',
      'Model Name': p.model_name || '',
      'Subtitle': p.subtitle || '',
      'Title': p.title || '',
      'Overview': p.overview || '',
      'Why We Love It': p.why_we_love_it || '',
      'Installation Type': p.installation_type || '',
      // Curation column for /bidets/recommended sections. Reintroduced
      // in migration 20260430220000_catalog_rebuild_v2_followup.sql.
      'Recommended Category': p.recommended_category || '',
      // Preserve the catalog-v2 enum for behavior that depends on the
      // product's electrical requirements (for example the GFCI guidance).
      power_source: p.power_source ?? null,
      primary_control_type: p.primary_control_type ?? null,
      compatible_toilet_shape: p.compatible_toilet_shape ?? null,
      // Old payloads may still carry these names while a catalog sync is
      // rolling forward. The live schema no longer has either column.
      requires_electricity_to_function: p.requires_electricity_to_function ?? null,
      has_required_remote_control: p.has_required_remote_control ?? null,
    };

    // Map all feature flags. Preserve null/undefined so the BidetModal can
    // distinguish three states: true (Included), false (Not Included),
    // null (Undetermined — we haven't verified this feature for this product).
    for (const [col, key] of Object.entries(FEATURE_FIELD_MAP)) {
      const v = p[col];
      mapped[key] = v === true || v === false ? v : null;
    }

    return mapped;
  });
}
